import {type ReactNode, useEffect, useState} from "react";
import {useIsLoggedIn, useUser} from "UserProvider";
import {useLocale, useMessages} from "i18n";
import {planOf, type Mode} from "home/gating";
import {PeelTag, PeelTagSlot} from "peel/PeelTag";
import {TagAction} from "peel/PeelTag.styles";
import {successGiftCandidates, type PeelCandidate} from "peel/peelTagSlot";
import {useCampaignRequestReserve} from "peel/useCampaignRequestReserve";
import {CouponClaimDialog} from "offers/ladder/CouponClaimDialog";
import {CurrentLadderGiftTag} from "offers/ladder/CurrentLadderGiftTag";
import {currentLadderAccountKey, currentLadderCurrency, currentLadderGift} from "offers/ladder/currentLadderOffer";
import {useCurrentLadderOffer} from "offers/ladder/useCurrentLadderOffer";
import {useCurrentLadderGiftActions} from "offers/ladder/useCurrentLadderGiftActions";
import {pricingCountryForContext} from "pricing/planCatalogue";
import {useResolvedStorefront} from "pricing/useQuotedPricing";
import {useReferralPrompt} from "referral/useReferralPrompt";
import type {ReferrerRewardStory} from "referral/referrerReward";
import {useInviteShare} from "referral/useInviteShare";
import {useMountedHostname} from "utils/useMountedHostname";
import {usePostSendOffer, type PostSendOffer as CampaignOffer} from "upsell/usePostSendOffer";
import {offerFirstMonthFigures, type OfferFirstMonthFigures} from "upsell/firstMonthPricing";
import {BAKED_PRICES} from "pricing/bakedCatalogue";
import {postSendMoment} from "upsell/offerEvents";
import {useOfferEvents} from "upsell/useOfferEvents";
import {recordDiscountPresented, hasPresentedDiscountToday, discountAccountKey} from "upsell/offerDismissal";
import {SuccessUpsellLine} from "./SuccessUpsellLine";
import type {PeerActivityListener, Unsubscribe} from "core/p2p/peerActivity";
import {usePeerActivity} from "./usePeerActivity";
import {hasServedDownload} from "./instantStatus";

/**
 * @param completedSendTransferId the transfer this success screen belongs to, which is the send
 *   evidence the coupon is issued against (api#1704). Set for hosted AND for P2P sends: a P2P link has
 *   no upload-finished moment on the server, so its creation is the send.
 */
export function SuccessGiftFrame({active, composing, composingBeside, mode = "upload", completedSendTransferId = null, subscribeActivity, children}: {
	active: boolean;
	/**
	 * The card's tags while it is NOT yet the success card (ui#2468): the download-limit card's host.
	 * The peel frame stays around the card in every phase, so a tag has a stacking context to hang
	 * behind the card in, rather than appearing only once a send has succeeded.
	 */
	composing: readonly PeelCandidate<ReactNode>[];
	/**
	 * The offer that hangs beside the card while it is NOT yet the success card (ui#2949): the
	 * trial-expiry offer. A slot of its own, inside the first one, because a slot shows ONE tag and the
	 * slide-out cards' host must never be displaced by it. Always rendered, even empty, so an offer
	 * arriving does not remount the card under it.
	 */
	composingBeside: readonly PeelCandidate<ReactNode>[];
	mode?: Mode; completedSendTransferId?: string | null; subscribeActivity: (listener: PeerActivityListener) => Unsubscribe; children: ReactNode;
}) {
	return active
		? <EligibleGiftFrame mode={mode} completedSendTransferId={completedSendTransferId} subscribeActivity={subscribeActivity}>{children}</EligibleGiftFrame>
		: <PeelTagSlot candidates={composing}><PeelTagSlot candidates={composingBeside}>{children}</PeelTagSlot></PeelTagSlot>;
}

function EligibleGiftFrame({mode, completedSendTransferId, subscribeActivity, children}: {
	mode: Mode; completedSendTransferId: string | null; subscribeActivity: (listener: PeerActivityListener) => Unsubscribe; children: ReactNode;
}) {
	const prompt = useReferralPrompt();
	const signedIn = useIsLoggedIn();
	const [user] = useUser();
	const accountKey = discountAccountKey(user);
	// Read ONCE, so a coupon presented on this very screen cannot silence the campaign offer that
	// outranks it in the slot (api#1704, owner 2026-09-15 — the rule lives in `upsell/firstMonthOffer`).
	const [seenToday] = useState(() => hasPresentedDiscountToday(accountKey));
	// The STOREFRONT locale, not the reading one (ui#2471): the two differ wherever a path prefix is only a
	// language, and this card's coupon must be quoted in the same money `useResolvedStorefront` below prices
	// the rest of the screen in.
	const {locale, storefrontLocale} = useLocale();
	const {pricing} = useMessages();
	const hostname = useMountedHostname();
	const storefront = useResolvedStorefront(signedIn);
	const activity = usePeerActivity(subscribeActivity);
	const campaign = usePostSendOffer(mode, hasServedDownload(activity), {seenToday});
	// The two prices the campaign line states (kitecorp/transferify-ui#2545), from the same storefront
	// the coupon beside it is quoted in. Null when the offer cannot be priced honestly, and then the
	// campaign takes no slot at all rather than describing an offer with no figures.
	const campaignFigures = campaign.offer === null || campaign.plan === null ? null
		: offerFirstMonthFigures(storefront.pricing, campaign.plan, campaign.offer.percentOff, locale, pricing.welcome.thenMonthly);
	const {offer, countdown, expired, expire, loading: couponLoading} = useCurrentLadderOffer(currentLadderAccountKey(signedIn, user, {
		// A CLAIMED campaign still holds the placement. Paying withdraws the row, so reading `offer` alone
		// would readmit the coupon the instant the sender bought (api#1704) and offer them the plan they
		// had just paid for, right through the `invoice.paid` window where `planOf(user)` still says free.
		hostname, campaignActive: campaign.offer !== null || campaign.claimed,
	}), pricingCountryForContext(storefrontLocale, hostname), completedSendTransferId, campaign.loading);
	// The coupon prices from the RESOLVED storefront where it can (ui#2545 review): the campaign line
	// beside it quotes the live storefront, and a reprice must not leave the two naming different renewal
	// prices for one tier. The baked snapshot still answers a coupon in another currency, which a paid
	// subscriber keeps across a storefront change.
	const gift = currentLadderGift(offer, currentLadderCurrency(offer, storefront.pricing.currency), planOf(user),
		{...BAKED_PRICES, [storefront.pricing.currency]: storefront.pricing});
	const actions = useCurrentLadderGiftActions(gift, accountKey);
	const slotCandidates = successGiftCandidates<ReactNode>(signedIn,
		gift === null ? null : <CurrentLadderGiftTag gift={gift} actions={actions} countdown={countdown} onExpired={expire}/>,
		prompt === null ? null : <SenderGift url={prompt.url} story={prompt.story}/>,
		campaignFigures === null ? null : <SuccessCampaign campaign={campaign} figures={campaignFigures} mode={mode} accountKey={accountKey}/>,
		null, actions.dismiss, couponLoading, expired,
	);
	// An unanswered campaign request reserves this slot, so no lower-ranked tag may APPEAR in it and be
	// displaced a moment later — while whatever the slot was already showing stays put (ui#2486,
	// ui#2492). The rule is `campaignRequestReserve` in `peel/peelTagSlot` and the memory it needs is
	// this hook's whole job; the coupon's own half is `couponAdmissionKey` in
	// `offers/ladder/couponCountdown`, which keeps this screen's settled answer through the same wait.
	const candidates = useCampaignRequestReserve(slotCandidates, campaign.loading);
	return <><PeelTagSlot candidates={candidates}>{children}</PeelTagSlot>
		{actions.dialogGift !== null && <CouponClaimDialog gift={actions.dialogGift} countdown={countdown}
			onClose={actions.closeDialog} onExpired={expire}/>}
	</>;
}

/** Only the selected campaign records an impression and suppresses the automatic exit offer. */
function SuccessCampaign({campaign, figures, mode, accountKey}: {campaign: CampaignOffer; figures: OfferFirstMonthFigures; mode: Mode; accountKey: string | null}) {
	const moment = postSendMoment(mode);
	const {reportClaimed} = useOfferEvents(moment, true, campaign.occasion);
	useEffect(() => recordDiscountPresented(accountKey), [accountKey]);
	return <SuccessUpsellLine offer={campaign.offer!} plan={campaign.plan!} figures={figures} spotsLeft={campaign.spotsLeft} moment={moment}
		occasion={campaign.occasion} endsOn={campaign.endsOn} mustNotNavigate={mode === "instant"}
		onClaimed={campaign.markClaimed} onClaimPressed={reportClaimed}/>;
}

/** The sender's own invite tag. `useInviteShare` is `useCopyFeedback` plus the funnel's `INVITE_SENT`. */
function SenderGift({url, story}: {url: string; story: ReferrerRewardStory}) {
	const {sender} = useMessages();
	const {copied, copy} = useInviteShare(url);
	return <PeelTag title={sender.referral.invite} copied={copied} action={
		<TagAction type="button" onClick={copy}>{copied ? sender.copied : sender.referral.getLink}</TagAction>
	}>{sender.referral.reward[story]}</PeelTag>;
}
